import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { useBuildingQuery } from 'app/data/queries/use-building-query';
import { Building } from 'app/data/types/building/building.types';
import { useSdkData } from 'app/shared/hooks/use-sdk-data/use-sdk-data.hook';
import { ErrorNotification } from 'app/shared/types/errors.types';

interface BuildingContextValue {
  building: Building | null;
  isLoading: boolean;
  error: ErrorNotification | null;
}

const BuildingContext = createContext<BuildingContextValue | undefined>(undefined);

export const BuildingProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const { sdkBuilding } = useSdkData();

  const { building, isLoading, error } = useBuildingQuery(sdkBuilding);

  const serializedBuilding = useMemo(() => ({
    building,
    isLoading,
    error,
  }),
  [building, isLoading, error]);

  return <BuildingContext.Provider value={serializedBuilding}>{children}</BuildingContext.Provider>;
};

export function useBuilding(): BuildingContextValue {
  const context = useContext(BuildingContext);
  if (!context) {
    throw new Error('useBuilding must be used within a BuildingProvider');
  }
  return context;
}
